import { useMemo } from "react";
import useOrganisation from "contexts/OrganisationContext/useOrganisation";

export interface UpsellEntitlements {
  /** The org pays for programming (Programs + Calendar). */
  hasOutputProgram: boolean;
  /** The org pays for the group monitoring bundle (Groups, Dashboards, Charting, Leaderboards). */
  hasGroupsAndMonitoring: boolean;
}

/**
 * Reads the org's *entitlements* for the two upsell bundles.
 *
 * @important This deliberately reads `featureConfig` rather than going through
 * `useCanDo`. `CanDoService` ANDs the org entitlement with the caller's role —
 * `group.any` is `OWNER`-only — so a `canDo` check would upsell Groups to a
 * coach in an org that already pays for it. The scope doc requires lock states
 * to be visible to coaches *and* org owners, so the gate has to be entitlement
 * only and leave role filtering where it already is.
 *
 * Driving off `featureConfig` also covers legacy orgs, which have no
 * `subscriptionDetails` at all and therefore no plan type to switch on — their
 * feature config *is* their plan config.
 */
function useUpsellEntitlements(): UpsellEntitlements {
  const organisation = useOrganisation();

  const hasOutputProgram = Boolean(organisation?.featureConfig?.isProgrammingEnabled);
  const hasGroupsAndMonitoring = Boolean(organisation?.featureConfig?.isGroupsEnabled);

  /**
   * Keyed on the two booleans rather than the organisation object, so the
   * returned object stays referentially stable when the org reference changes
   * but the entitlements have not.
   */
  return useMemo(() => ({ hasOutputProgram, hasGroupsAndMonitoring }), [hasOutputProgram, hasGroupsAndMonitoring]);
}

export default useUpsellEntitlements;
